import React, { useCallback, useEffect, useState } from "react";

// Picks an IGDB platform for the console flow — used by both Consoles tabs. The search
// runs through /admin/consoles/igdb-platform-search: IGDB's own answer (server-side
// credentials; unavailable without IGDB_CLIENT_SECRET) plus every IGDB platform already
// in use here, which works without IGDB at all.
//
// `allowNone`: offers "No IGDB platform" — an override can say IGDB has none for a
// console. A platform link can't (removing it is its own action on the tab).
function IgdbPlatformPickerModal({ title, subtitle, initialQuery, allowNone, apiFetch, onPick, onClose }) {
  const [query, setQuery] = useState(initialQuery || "");
  const [results, setResults] = useState(null);
  const [known, setKnown] = useState([]);
  const [igdbError, setIgdbError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const search = useCallback(async (text) => {
    const trimmed = text.trim();
    if (trimmed.length < 2) return;
    setSearching(true);
    setError(null);
    setSelected(null);
    try {
      const data = await apiFetch(`/admin/consoles/igdb-platform-search?q=${encodeURIComponent(trimmed)}`);
      setResults(data.results);
      setKnown(data.known);
      setIgdbError(data.igdb_error);
    } catch (err) {
      if (err.message !== "Forbidden") setError("Platform search failed");
    } finally {
      setSearching(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (initialQuery) search(initialQuery);
  }, [search, initialQuery]);

  function handleSearch(e) {
    e.preventDefault();
    search(query);
  }

  async function save() {
    if (selected === null) return;
    setSaving(true);
    setError(null);
    try {
      await onPick(selected === "none" ? null : selected);
      onClose();
    } catch (err) {
      if (err.message !== "Forbidden") setError("Couldn't save");
    } finally {
      setSaving(false);
    }
  }

  const igdbIds = new Set((results || []).map((p) => p.id));
  const knownOnly = known.filter((p) => !igdbIds.has(p.id));

  function renderOption(platform, tag) {
    const isSelected = selected !== "none" && selected?.id === platform.id;
    return (
      <button
        key={`${tag}-${platform.id}`}
        type="button"
        className={`admin-igdb-result admin-console-platform-option ${isSelected ? "admin-igdb-result--selected" : ""}`}
        onClick={() => setSelected(platform)}
      >
        <div className="admin-row-main">
          <div className="admin-source-name">
            {platform.name}
            {platform.abbreviation && <code className="admin-barcode-chip admin-platform-abbr">{platform.abbreviation}</code>}
          </div>
          <div className="admin-source-meta">igdb_platform_id {platform.id} · slug <code>{platform.slug}</code></div>
        </div>
      </button>
    );
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal--wide admin-card glass" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 className="admin-modal-title">{title}</h3>
        {subtitle && <div className="admin-source-meta">{subtitle}</div>}

        <form className="admin-tgdb-link-form admin-igdb-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="IGDB platform name or abbreviation"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="admin-tgdb-link-input"
            autoFocus
          />
          <button type="submit" className="admin-signout-btn" disabled={searching || query.trim().length < 2}>
            {searching ? "Searching…" : "Search"}
          </button>
        </form>

        {igdbError && (
          <p className="admin-status admin-error admin-status--inline">
            IGDB isn't reachable from the server (check IGDB_CLIENT_SECRET) — only platforms already in use are listed.
          </p>
        )}

        <div className="admin-igdb-search-results">
          {results && results.length === 0 && knownOnly.length === 0 && !searching && (
            <p className="admin-status">No platforms found.</p>
          )}
          {results && results.map((p) => renderOption(p, "igdb"))}
          {knownOnly.length > 0 && (
            <>
              <div className="admin-row-id admin-console-picker-heading">Already in use</div>
              {knownOnly.map((p) => renderOption(p, "known"))}
            </>
          )}
          {allowNone && (
            <button
              type="button"
              className={`admin-igdb-result admin-console-platform-option ${selected === "none" ? "admin-igdb-result--selected" : ""}`}
              onClick={() => setSelected("none")}
            >
              <div className="admin-row-main">
                <div className="admin-source-name">No IGDB platform</div>
                <div className="admin-source-meta">IGDB doesn't list this console — the app gets igdb_platform null</div>
              </div>
            </button>
          )}
        </div>

        {error && <div className="admin-status admin-error admin-status--inline">{error}</div>}

        <div className="admin-igdb-link-footer">
          <span className="admin-row-id">
            {selected === "none" ? "No IGDB platform" : selected ? `${selected.name} (${selected.id})` : "Pick a platform"}
          </span>
          <button type="button" className="admin-signout-btn" onClick={save} disabled={selected === null || saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IgdbPlatformPickerModal;
